import { Node, Edge, MarkerType } from '@xyflow/react';
import dagre from 'dagre';
import { GraphDefinition, GraphNode, NodeType } from '../types';

// Node dimensions:
const NODE_WIDTH = 120;
const NODE_HEIGHT = 100;

// Layout configuration:
const HORIZONTAL_SPACING = 280;
const VERTICAL_SPACING = 140;
const START_X = 100;
const START_Y = 350;

/**
 * Convert backend node type to ReactFlow node type
 * Also checks node name for START/END, since backend may use 'basic' type
 */
export const getNodeType = (nodeType: NodeType, nodeName?: string): string => {
  // Check name first for START/END nodes
  const upperName = nodeName?.toUpperCase();
  if (upperName === 'START' || upperName === '__START__') return 'start';
  if (upperName === 'END' || upperName === '__END__') return 'end';
  
  switch (nodeType) {
    case 'agent':
      return 'agent';
    case 'tool':
      return 'tool';
    case 'custom_tool':
      return 'custom_tool';
    case 'mcp_server':
      return 'mcp_server';
    case 'start':
      return 'start';
    case 'end':
      return 'end';
    default:
      return 'generic';
  }
};

/**
 * Get vulnerability count for a node
 */
export const getVulnerabilityCount = (node: GraphNode): number => {
  if ('vulnerabilities' in node && Array.isArray(node.vulnerabilities)) {
    return node.vulnerabilities.length;
  }
  return 0;
};

/**
 * Get recommended guardrails for a node
 */
export const getGuardrails = (node: GraphNode): string[] => {
  if ('vulnerabilities' in node && Array.isArray(node.vulnerabilities)) {
    const guardrails: string[] = [];
    node.vulnerabilities.forEach((vuln: any) => {
      if (vuln.guardrails && Array.isArray(vuln.guardrails)) {
        vuln.guardrails.forEach((g: any) => {
          if (g.name && !guardrails.includes(g.name)) {
            guardrails.push(g.name);
          }
        });
      }
    });
    return guardrails;
  }
  return [];
};

const isStartNode = (n: Node) =>
    n.type === 'start' ||
    n.data?.label?.toUpperCase() === 'START' ||
    n.data?.label?.toUpperCase() === '__START__';

const isEndNode = (n: Node) =>
    n.type === 'end' ||
    n.data?.label?.toUpperCase() === 'END' ||
    n.data?.label?.toUpperCase() === '__END__';

/**
 * Add virtual START and END nodes if they don't exist in the graph
 */
export const ensureStartEndNodes = (
  nodes: Node[],
  edges: Edge[]
): { nodes: Node[]; edges: Edge[] } => {
  const hasStart = nodes.some(isStartNode);
  const hasEnd = nodes.some(isEndNode);

  const newNodes = [...nodes];
  const newEdges = [...edges];

  if (!hasStart) {
    // Find nodes with no incoming edges (entry points)
    const nodesWithIncoming = new Set(edges.map(e => e.target));
    const entryNodes = nodes.filter(n => !nodesWithIncoming.has(n.id));

    // Create virtual START node
    const startNode: Node = {
      id: '__virtual_start__',
      type: 'start',
      position: { x: 0, y: 0 },
      data: { label: 'START', isStart: true },
    };
    newNodes.push(startNode);

    // Connect START to entry nodes (or first agent if no clear entry)
    const targets = entryNodes.length > 0 
      ? entryNodes 
      : nodes.filter(n => n.type === 'agent').slice(0, 1);
    
    targets.forEach((target, idx) => {
      newEdges.push({
        id: `virtual-start-edge-${idx}`,
        source: '__virtual_start__',
        target: target.id,
        type: 'workflow',
        data: { hasVulnerability: false, guardrails: [] },
      });
    });
  }

  if (!hasEnd) {
    // Find nodes with no outgoing edges (exit points)
    const nodesWithOutgoing = new Set(edges.map(e => e.source));
    const exitNodes = nodes.filter(n => !nodesWithOutgoing.has(n.id) && !isStartNode(n));

    // Create virtual END node
    const endNode: Node = {
      id: '__virtual_end__',
      type: 'end',
      position: { x: 0, y: 0 },
      data: { label: 'END', isStart: false },
    };
    newNodes.push(endNode);

    // Connect exit nodes to END (or last tools if no clear exit)
    const sources = exitNodes.length > 0 
      ? exitNodes 
      : nodes.filter(n => n.type === 'tool' || n.type === 'custom_tool').slice(-1);
    
    sources.forEach((source, idx) => {
      newEdges.push({
        id: `virtual-end-edge-${idx}`,
        source: source.id,
        target: '__virtual_end__',
        type: 'workflow',
        data: { hasVulnerability: false, guardrails: [] },
      });
    });
  }

  return { nodes: newNodes, edges: newEdges };
};

/**
 * Apply dagre layout - properly handles edge routing and minimizes crossings
 */
export const applyHierarchicalLayout = (
  nodes: Node[],
  edges: Edge[],
  graphData: GraphDefinition
): { positionedNodes: Node[]; backEdges: Set<string> } => {
  if (nodes.length === 0) return { positionedNodes: nodes, backEdges: new Set() };

  // Create dagre graph
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  
  // Configure the layout
  dagreGraph.setGraph({ 
    rankdir: 'LR',      // Left to right
    nodesep: 100,        // Vertical spacing between nodes
    ranksep: 250,       // Horizontal spacing between columns
    marginx: 40,
    marginy: 40,
    acyclicer: 'greedy',  // Handle cycles from loop back-edges
  });

  // Add nodes to dagre
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  // Add edges to dagre - track which ones are back edges
  const { layoutEdges, backEdgeIds } = identifyBackEdges(edges);
  layoutEdges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Run the layout
  dagre.layout(dagreGraph);

  // Apply positions back to nodes
  const positionedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });

  return { positionedNodes, backEdges: backEdgeIds };
};




const filterBackEdges = (edges: Edge[]): Edge[] => {
  const adj = new Map<string, Set<string>>();
  const result: Edge[] = [];

  for (const edge of edges) {
    if (hasPath(adj, edge.target, edge.source)) {
      continue;
    }
    result.push(edge);
    if (!adj.has(edge.source)) adj.set(edge.source, new Set());
    adj.get(edge.source)!.add(edge.target);
  }

  return result;
};

const hasPath = (
  adj: Map<string, Set<string>>,
  from: string,
  to: string
): boolean => {
  const visited = new Set<string>();
  const stack = [from];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (node === to) return true;
    if (visited.has(node)) continue;
    visited.add(node);
    const neighbors = adj.get(node);
    if (neighbors) {
      neighbors.forEach(n => stack.push(n));
    }
  }
  return false;
};


/**
 * Connect existing START/END nodes if they have no edges
 */
export const connectDisconnectedStartEnd = (
  nodes: Node[],
  edges: Edge[]
): Edge[] => {
  const newEdges = [...edges];

  const startNode = nodes.find(isStartNode);
  const endNode = nodes.find(isEndNode);

  const nodesWithIncoming = new Set(edges.map(e => e.target));
  const nodesWithOutgoing = new Set(edges.map(e => e.source));

  // Connect START if it exists but has no outgoing edges
  if (startNode && !nodesWithOutgoing.has(startNode.id)) {
    const entryNodes = nodes.filter(n => 
      !isStartNode(n) && 
      !isEndNode(n) && 
      !nodesWithIncoming.has(n.id)
    );
    const targets = entryNodes.length > 0
      ? entryNodes
      : nodes.filter(n => n.type === 'agent');

    targets.forEach((target, idx) => {
      newEdges.push({
        id: `start-connect-${idx}`,
        source: startNode.id,
        target: target.id,
        type: 'workflow',
        data: { hasVulnerability: false, vulnerabilityCount: 0, guardrails: [] },
      });
    });
  }

  // Connect END if it exists but has no incoming edges
  if (endNode && !nodesWithIncoming.has(endNode.id)) {
    const exitNodes = nodes.filter(n =>
      !isStartNode(n) &&
      !isEndNode(n) &&
      !nodesWithOutgoing.has(n.id)
    );
    const sources = exitNodes.length > 0
      ? exitNodes
      : nodes.filter(n => n.type === 'mcp_server' || n.type === 'tool' || n.type === 'custom_tool');

    sources.forEach((source, idx) => {
      newEdges.push({
        id: `end-connect-${idx}`,
        source: source.id,
        target: endNode.id,
        type: 'workflow',
        data: { hasVulnerability: false, vulnerabilityCount: 0, guardrails: [] },
      });
    });
  }

  return newEdges;
};

/**
 * Determine the best handles for an edge based on node positions
 * Returns { sourceHandle, targetHandle } based on relative positions
 * 
 * Handle IDs in nodes:
 * - Left:   target="left", source="left-source"
 * - Right:  source="right", target="right-target"
 * - Top:    target="top", source="top-source"
 * - Bottom: source="bottom", target="bottom-target"
 */
export const getOptimalHandles = (
  sourceNode: Node,
  targetNode: Node
): { sourceHandle: string; targetHandle: string } => {
  const sourceX = sourceNode.position.x;
  const sourceY = sourceNode.position.y;
  const targetX = targetNode.position.x;
  const targetY = targetNode.position.y;

  const dx = targetX - sourceX;
  const dy = targetY - sourceY;

  // Determine primary direction
  const isHorizontal = Math.abs(dx) > Math.abs(dy);

  if (isHorizontal) {
    if (dx > 0) {
      // Target is to the RIGHT of source
      // Source exits from right (source handle), target enters from left (target handle)
      return { sourceHandle: 'right', targetHandle: 'left' };
    } else {
      // Target is to the LEFT of source (back edge)
      // Source exits from left (need source handle), target enters from right (need target handle)
      return { sourceHandle: 'left-source', targetHandle: 'right-target' };
    }
  } else {
    if (dy > 0) {
      // Target is BELOW source
      // Source exits from bottom (source handle), target enters from top (target handle)
      return { sourceHandle: 'bottom', targetHandle: 'top' };
    } else {
      // Target is ABOVE source
      // Source exits from top (need source handle), target enters from bottom (need target handle)
      return { sourceHandle: 'top-source', targetHandle: 'bottom-target' };
    }
  }
};


/**
 * Identify back edges without removing them from the final edge list.
 * Back edges are excluded from dagre layout (to avoid cycles) but included in final rendering.
 * Returns:
 * - layoutEdges: edges to pass to dagre (excludes back edges to prevent layout issues)
 * - allEdges: all edges including back edges (for final rendering)
 * - backEdgeIds: set of back edge IDs for special styling
 */
const identifyBackEdges = (edges: Edge[]): { 
  layoutEdges: Edge[]; 
  backEdgeIds: Set<string> 
} => {
  const adj = new Map<string, Set<string>>();
  const layoutEdges: Edge[] = [];
  const backEdgeIds = new Set<string>();

  for (const edge of edges) {
    // Check if adding this edge would create a cycle
    // (i.e., there's already a path from target back to source)
    if (hasPath(adj, edge.target, edge.source)) {
      // This is a back edge - mark it, exclude from dagre layout
      backEdgeIds.add(edge.id);
      // Don't add to layoutEdges - dagre can't handle cycles
    } else {
      layoutEdges.push(edge);
      if (!adj.has(edge.source)) adj.set(edge.source, new Set());
      adj.get(edge.source)!.add(edge.target);
    }
  }

  return { layoutEdges, backEdgeIds };
};

/**
 * Assign optimal handles to all edges based on node positions
 */
export const assignEdgeHandles = (
  nodes: Node[],
  edges: Edge[],
  backEdgeIds?: Set<string>
): Edge[] => {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  // FIRST PASS: Count edges between each node pair
  const edgePairCount = new Map<string, number>();
  edges.forEach(edge => {
    const pairKey = [edge.source, edge.target].sort().join('|');
    edgePairCount.set(pairKey, (edgePairCount.get(pairKey) || 0) + 1);
  });

  // SECOND PASS: Track index within each pair
  const edgePairIndex = new Map<string, number>();

  return edges.map(edge => {
    const sourceNode = nodeMap.get(edge.source);
    const targetNode = nodeMap.get(edge.target);

    const markerEnd = {
      type: MarkerType.ArrowClosed,
      width: 20,
      height: 20,
      color: '#49495B',
    };

    if (sourceNode && targetNode) {
      const { sourceHandle, targetHandle } = getOptimalHandles(sourceNode, targetNode);
      
      const isBackEdge = backEdgeIds?.has(edge.id) || 
        (targetNode.position.x + 50) < sourceNode.position.x;

      // Calculate curve offset for bidirectional edges
      const pairKey = [edge.source, edge.target].sort().join('|');
      const totalInPair = edgePairCount.get(pairKey) || 1;
      
      // Get current index and increment for next edge in same pair
      const currentIndex = edgePairIndex.get(pairKey) || 0;
      edgePairIndex.set(pairKey, currentIndex + 1);

      // Calculate offset: spread edges apart like an eye shape
      let curveOffset = 0;
      if (totalInPair === 2) {
        // Two edges: one curves up (-50), one curves down (+50)
        curveOffset = currentIndex === 0 ? -50 : 50;
      } else if (totalInPair > 2) {
        // More than 2: spread evenly
        const step = 80;
        curveOffset = (currentIndex - (totalInPair - 1) / 2) * step;
      }

      return {
        ...edge,
        sourceHandle,
        targetHandle,
        markerEnd,
        data: {
          ...edge.data,
          isBackEdge,
          curveOffset,
        },
      };
    }

    return { ...edge, markerEnd };
  });
};